/**
 * C3 scaffold — Meet-your-Crew Mate pick + display name draft
 *
 * Persists under pulse_preferences.card_type_preferences.meta.crew_member_draft
 * (same jsonb nest as interest_brief / briefing_schedule — no migration).
 *
 * Honesty boundaries (do not weaken):
 * - BOUND only with a real helperId UUID — never invent helperId / fake Helper rows
 * - Assistants catalog is the Helper-id source; no invent createHelper endpoint
 * - UI may save Mate + displayName unbound — Crew Chat is a separate surface
 *
 * No Muse/Vesper. Preserve LosiAnimated. Metro cannot import this file —
 * mobile mirrors in mobile/lib/crew/member-bind.ts (+ mates catalog lockstep).
 *
 * Dependencies: mates (MateId / resolveMateId / DEFAULT_MATE_ID)
 */

import {
  DEFAULT_MATE_ID,
  MATE_BY_ID,
  resolveMateId,
  type MateId
} from '@/lib/crew/mates'

/** Explicit scaffold state — never claim a live Helper bind from prefs alone. */
export const CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET = 'NOT_BOUND_YET' as const

/** Live Helper / Member Runner bind (C3). Requires helperId. */
export const CREW_MEMBER_BIND_STATUS_BOUND = 'BOUND' as const

export type CrewMemberBindStatus =
  | typeof CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
  | typeof CREW_MEMBER_BIND_STATUS_BOUND

/** Prefs meta key — nest under card_type_preferences.meta. */
export const CREW_MEMBER_DRAFT_META_KEY = 'crew_member_draft' as const

/** Top-level PUT alias — merged into meta.crew_member_draft on write. */
export const CREW_MEMBER_DRAFT_ALIAS = 'crew_member_draft' as const

/** Display name length cap (spaces count). */
export const CREW_MEMBER_DISPLAY_NAME_MAX = 40

/** Section title — draft face for chat (not owned Command members). */
export const CREW_MEET_YOUR_CREW_TITLE = 'Meet your Crew'

/** Short supporting line under the title. */
export const CREW_MEET_YOUR_CREW_HINT =
  'Pick a face and name. Link a Helper so they can use your Assistants.' as const

/**
 * Honesty banner — short warm line (no C3 / NOT_BOUND_YET jargon).
 * Hosts must gate mounts when empty.
 */
export const CREW_MEET_YOUR_CREW_HONESTY =
  'Helper is optional — chat works without one.' as const

/** Visible status chip when draft has no Helper linked. */
export const CREW_MEMBER_BIND_STATUS_LABEL = 'Not linked' as const

/**
 * Mate grid accessible name — silenced when the section heading already
 * names the group (anti double-speak with CREW_MEET_YOUR_CREW_TITLE).
 * Hosts must omit aria-label / accessibilityLabel when empty.
 */
export const CREW_MEET_YOUR_CREW_MATE_GROUP_A11Y = '' as const

/** Display name field label. */
export const CREW_MEET_YOUR_CREW_NAME_LABEL = 'Name'

/** Display name placeholder — ends with … (Web Interface Guidelines). */
export const CREW_MEET_YOUR_CREW_NAME_PLACEHOLDER = ''

/** Form field name — autofill hygiene. */
export const CREW_MEET_YOUR_CREW_NAME_FIELD = 'crew_member_display_name' as const

/** Helper bind field label (Assistants catalog + optional id). */
export const CREW_MEET_YOUR_CREW_HELPER_LABEL = 'Helper'

/** Helper select field id / name. */
export const CREW_MEET_YOUR_CREW_HELPER_FIELD = 'crew_member_helper_id' as const

/** Empty option in Helper picker. */
export const CREW_MEET_YOUR_CREW_HELPER_NONE = 'None' as const

/** Manual id option when the catalog does not list the Helper. */
export const CREW_MEET_YOUR_CREW_HELPER_OTHER = 'Other…' as const

/**
 * Manual id input placeholder — silenced (anti-slop; no UUID jargon).
 * Hosts must omit placeholder when empty.
 */
export const CREW_MEET_YOUR_CREW_HELPER_UUID_PLACEHOLDER = '' as const

/**
 * Link to Assistants (Helper id source). Not a create CTA — /assistants is
 * the manage surface (anti-slop; do not promise Create when the href is list).
 */
export const CREW_MEET_YOUR_CREW_HELPER_CREATE_LABEL = 'Assistants…' as const

/** Href for Assistants manage (no invent createHelper). */
export const CREW_MEET_YOUR_CREW_HELPER_CREATE_HREF = '/assistants' as const

/**
 * Bound-status product chrome — plain English (no BOUND enum tokens).
 * Hosts must gate mounts when empty.
 */
export const CREW_MEET_YOUR_CREW_HELPER_BOUND_LABEL = 'Helper linked' as const

/** Save draft CTA. */
export const CREW_MEET_YOUR_CREW_SAVE_LABEL = 'Save'

/** Save draft a11y. */
export const CREW_MEET_YOUR_CREW_SAVE_A11Y = 'Save Meet your Crew' as const

/** Toast / Alert — load failure (what failed; no HTTP/enum jargon). */
export const CREW_MEET_YOUR_CREW_LOAD_ERROR =
  'Could not load Meet your Crew' as const

/** Toast / Alert — save failure (what failed; no HTTP/enum jargon). */
export const CREW_MEET_YOUR_CREW_SAVE_ERROR =
  'Could not save Meet your Crew' as const

/**
 * Toast / Alert — save success. Hosts must gate toast when empty.
 */
export const CREW_MEET_YOUR_CREW_SAVE_SUCCESS = 'Saved.' as const

/**
 * Closed chrome set (web ↔ mobile lockstep).
 * Prefer named exports at call sites; MIRROR is for parity audits.
 */
export const CREW_MEET_YOUR_CREW_MIRROR = {
  CREW_MEET_YOUR_CREW_TITLE,
  CREW_MEET_YOUR_CREW_HINT,
  CREW_MEET_YOUR_CREW_HONESTY,
  CREW_MEMBER_BIND_STATUS_LABEL,
  CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET,
  CREW_MEET_YOUR_CREW_MATE_GROUP_A11Y,
  CREW_MEET_YOUR_CREW_NAME_LABEL,
  CREW_MEET_YOUR_CREW_NAME_PLACEHOLDER,
  CREW_MEET_YOUR_CREW_NAME_FIELD,
  CREW_MEET_YOUR_CREW_HELPER_LABEL,
  CREW_MEET_YOUR_CREW_HELPER_FIELD,
  CREW_MEET_YOUR_CREW_HELPER_NONE,
  CREW_MEET_YOUR_CREW_HELPER_OTHER,
  CREW_MEET_YOUR_CREW_HELPER_UUID_PLACEHOLDER,
  CREW_MEET_YOUR_CREW_HELPER_CREATE_LABEL,
  CREW_MEET_YOUR_CREW_HELPER_CREATE_HREF,
  CREW_MEET_YOUR_CREW_HELPER_BOUND_LABEL,
  CREW_MEET_YOUR_CREW_SAVE_LABEL,
  CREW_MEET_YOUR_CREW_SAVE_A11Y,
  CREW_MEET_YOUR_CREW_LOAD_ERROR,
  CREW_MEET_YOUR_CREW_SAVE_ERROR,
  CREW_MEET_YOUR_CREW_SAVE_SUCCESS,
  CREW_MEMBER_DISPLAY_NAME_MAX
} as const

/**
 * Draft / bind shape stored in prefs.
 * helperId is required when bindStatus === BOUND — never invent one.
 */
export type CrewMemberDraft = {
  mateId: MateId
  displayName: string
  bindStatus: CrewMemberBindStatus
  /** Live Helper id when BOUND (UUID). Absent while NOT_BOUND_YET. */
  helperId?: string | null
}

const HELPER_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function normalizeCrewMemberHelperId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!HELPER_ID_UUID_RE.test(trimmed)) return null
  return trimmed
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/** Normalize display name — trim + cap; empty → ''. */
export function normalizeCrewMemberDisplayName(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  return raw.trim().slice(0, CREW_MEMBER_DISPLAY_NAME_MAX)
}

/**
 * Normalize any raw draft-like input into an honest CrewMemberDraft.
 * BOUND only when helperId is a valid UUID — otherwise NOT_BOUND_YET
 * (never invent helperId).
 */
export function normalizeCrewMemberDraft(
  raw: unknown
): CrewMemberDraft | null {
  if (raw == null) return null
  if (!isRecord(raw)) return null

  const mateId = resolveMateId(
    typeof raw.mateId === 'string'
      ? raw.mateId
      : typeof raw.mate_id === 'string'
        ? raw.mate_id
        : null
  )
  const displayName = normalizeCrewMemberDisplayName(
    raw.displayName ?? raw.display_name
  )
  const helperId = normalizeCrewMemberHelperId(
    raw.helperId ?? raw.helper_id
  )
  const wantsBound =
    raw.bindStatus === CREW_MEMBER_BIND_STATUS_BOUND ||
    raw.bind_status === CREW_MEMBER_BIND_STATUS_BOUND

  if (!mateId || !MATE_BY_ID.has(mateId)) {
    if (!displayName && !mateId) return null
    return {
      mateId: mateId ?? DEFAULT_MATE_ID,
      displayName,
      bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
    }
  }

  if (wantsBound && helperId) {
    return {
      mateId,
      displayName,
      bindStatus: CREW_MEMBER_BIND_STATUS_BOUND,
      helperId
    }
  }

  return {
    mateId,
    displayName,
    bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
  }
}

/**
 * Bind a Mate draft to a real Helper id (C3). Invalid helperId → unbound draft.
 */
export function bindCrewMemberDraftToHelper(
  draft: CrewMemberDraft | null | undefined,
  helperId: unknown
): CrewMemberDraft {
  const base = draft ?? defaultCrewMemberDraft()
  const id = normalizeCrewMemberHelperId(helperId)
  if (!id) {
    return {
      mateId: base.mateId,
      displayName: base.displayName,
      bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
    }
  }
  return {
    mateId: base.mateId,
    displayName: base.displayName,
    bindStatus: CREW_MEMBER_BIND_STATUS_BOUND,
    helperId: id
  }
}

/** Empty picker default — petal + empty name + NOT_BOUND_YET. */
export function defaultCrewMemberDraft(): CrewMemberDraft {
  return {
    mateId: DEFAULT_MATE_ID,
    displayName: '',
    bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
  }
}

/**
 * Read crew_member_draft from card_type_preferences jsonb.
 * Prefer normalizeCrewMemberDraft for BOUND-with-UUID honesty — never invent helperId.
 */
export function extractCrewMemberDraft(
  cardTypePreferences: unknown
): CrewMemberDraft | null {
  if (!isRecord(cardTypePreferences)) return null
  const meta = cardTypePreferences.meta
  if (!isRecord(meta)) return null
  return normalizeCrewMemberDraft(meta[CREW_MEMBER_DRAFT_META_KEY])
}

/**
 * Merge a Mate draft into card_type_preferences without dropping toggles
 * or sibling meta (interest_brief / briefing_schedule).
 * Pass null to clear. Always writes bindStatus = NOT_BOUND_YET.
 */
export function mergeCrewMemberDraft(
  existing: unknown,
  draft: CrewMemberDraft | null
): Record<string, unknown> {
  const base: Record<string, unknown> = isRecord(existing) ? { ...existing } : {}
  const priorMeta = isRecord(base.meta) ? { ...base.meta } : {}

  if (draft == null) {
    delete priorMeta[CREW_MEMBER_DRAFT_META_KEY]
    if (Object.keys(priorMeta).length === 0) {
      delete base.meta
    } else {
      base.meta = priorMeta
    }
    return base
  }

  const normalized = normalizeCrewMemberDraft(draft) ?? defaultCrewMemberDraft()
  const stored: CrewMemberDraft =
    normalized.bindStatus === CREW_MEMBER_BIND_STATUS_BOUND &&
    normalized.helperId
      ? {
          mateId: normalized.mateId,
          displayName: normalized.displayName,
          bindStatus: CREW_MEMBER_BIND_STATUS_BOUND,
          helperId: normalized.helperId
        }
      : {
          mateId: normalized.mateId,
          displayName: normalized.displayName,
          bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
        }

  base.meta = {
    ...priorMeta,
    [CREW_MEMBER_DRAFT_META_KEY]: stored
  }
  return base
}

/** Zod-friendly plain object for API bodies. */
export function crewMemberDraftToJson(
  draft: CrewMemberDraft
): Record<string, string> {
  const normalized = normalizeCrewMemberDraft(draft) ?? defaultCrewMemberDraft()
  if (
    normalized.bindStatus === CREW_MEMBER_BIND_STATUS_BOUND &&
    normalized.helperId
  ) {
    return {
      mateId: normalized.mateId,
      displayName: normalized.displayName,
      bindStatus: CREW_MEMBER_BIND_STATUS_BOUND,
      helperId: normalized.helperId
    }
  }
  return {
    mateId: normalized.mateId,
    displayName: normalized.displayName,
    bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
  }
}
